// Which configuration each route opens with, and where its element sets come from.

// A source is either a bare GP group name (resolved against the probed GP base,
// worker `/api/gp/<name>.json` or the static `data/gp/<name>.json` snapshot) or
// an explicit URL/path (anything containing "/" or ".", incl. legacy .txt),
// which passes through unchanged and is parsed via payload sniffing.
export type ElementsEntry = [source: string, tags: string[]];

export interface PresetConfig {
  sat?: {
    enabledTags?: string[];
    enabledComponents?: string[];
    overpassMode?: string;
  };
  cesium?: {
    layers?: string[];
    // One of SCENE_MODES. No preset sets it yet; it is here so that a route can
    // open straight into the sky view without the type having to change first,
    // which is the point of a preset supplying defaults.
    sceneMode?: string;
    // One of SURFACE_MODELS, and unset for the same reason: a route that opens on
    // the sky view is the one that would want buildings with it.
    surfaceModel?: string;
  };
}

export interface Preset {
  title: string;
  description?: string;
  config: PresetConfig;
  elements: ElementsEntry[];
}

export const presets: Record<string, Preset> = {
  default: {
    title: "Satvis - 3D Satellite Tracker & Sky View",
    config: {
      sat: {
        enabledTags: ["Weather"],
      },
    },
    // Bare group names matching worker/src/config/satvis.core.yaml.
    elements: [
      ["cubesat", ["Cubesat"]],
      ["globalstar", ["Globalstar"]],
      ["gnss", ["GNSS"]],
      ["iridium-NEXT", ["IridiumNEXT"]],
      ["last-30-days", ["New"]],
      ["oneweb", ["OneWeb"]],
      ["planet", ["Planet"]],
      ["resource", ["Resource"]],
      ["science", ["Science"]],
      ["spire", ["Spire"]],
      ["starlink", ["Starlink"]],
      ["stations", ["Stations"]],
      ["weather", ["Weather"]],
      ["eutelsat", ["Eutelsat"]],
    ],
  },
  ot: {
    title: "OT Satvis - 3D Satellite Tracker & Sky View",
    config: {
      sat: {
        enabledTags: ["OT"],
        enabledComponents: ["Point", "Label", "Orbit", "Sensor cone", "Ground track"],
        overpassMode: "swath",
      },
      cesium: {
        layers: ["VersaTiles"],
      },
    },
    elements: [
      ["ot", ["OT"]],
      ["wfs", ["WFS"]],
    ],
  },
};

export function getConfigPreset(path: string = window.location.pathname): Preset {
  const routeName = (path.split("/").pop() ?? "").replace(/\.html$/, "");

  switch (routeName) {
    case "ot":
      return presets.ot as Preset;
    default:
      return presets.default as Preset;
  }
}

export function updateMetadata(preset: Preset): void {
  document.title = preset.title;
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription && preset.description) {
    metaDescription.setAttribute("content", preset.description);
  }
}
